import { yupResolver } from '@hookform/resolvers/yup';
import { AxiosError, AxiosResponse } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { editUser, fetchDepartmentsPaginated, getEmployeePositions } from 'services/registration';
import { APIErrors } from 'types/api';
import { employeesInterface } from 'types/employee';
import { EditUserDataValue, EditUserRequest } from './index';
import { schema } from './validation';

const defaultValues: EditUserDataValue = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '000000000',
    userIds: '',
    employee_position_id: null,
    department_id: '',
};

interface UseEditUserDataFormProps {
    user: employeesInterface[];
    workplaceId: number;
    onCancel: (success: boolean) => void;
}

export const useEditUserDataForm = ({ user, workplaceId, onCancel }: UseEditUserDataFormProps) => {
    const { t } = useTranslation();

    const [selectedDepartment, setSelectedDepartment] = useState<{ label: string; value: number } | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<{ label: string; value: number } | null>(null);

    const {
        register,
        handleSubmit,
        setError,
        setValue,
        control,
        formState: { errors },
    } = useForm<EditUserDataValue>({
        resolver: yupResolver(schema),
        defaultValues,
    });

    const editUserData = useMutation<
        AxiosResponse<{ message: string }>,
        AxiosError<APIErrors>,
        EditUserRequest
    >((payload) => editUser(payload), {
        onSuccess: () => {
            toast.success(t('employeeManagement.editSuccessMessage'));
            onCancel(true);
        },
        onError: (error) => {
            const formErrors = error?.response?.data?.errors;
            if (formErrors) {
                Object.entries(formErrors).forEach(([key, value]) => {
                    setError(key as keyof EditUserDataValue, {
                        message: value?.[0],
                    });
                });
            }
        },
    });

    const onSubmit = (formObject: EditUserDataValue) => {
        const currentUser = user[0];
        const prepareData = { ...formObject } as any;
        prepareData['workplace_id'] = workplaceId;
        prepareData['has_elearning_permission'] = currentUser.has_elearning_permission;
        editUserData.mutate({ data: prepareData });
    };

    const loadPositionOptions = async (search: string, _: any, { page }: { page: number }) => {
        const res = await getEmployeePositions(page, search || undefined);
        return {
            options: res.data.map((p) => ({ label: p.name, value: p.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    const loadDepartmentOptions = async (_search: string, _: any, { page }: { page: number }) => {
        const res = await fetchDepartmentsPaginated(workplaceId, page);
        return {
            options: res.data.map((d: any) => ({ label: d.name, value: d.id })),
            hasMore: res.current_page < res.last_page,
            additional: { page: page + 1 },
        };
    };

    useEffect(() => {
        const currentUser = user[0];
        if (!currentUser) return;

        setValue('first_name', currentUser.first_name || '');
        setValue('last_name', currentUser.last_name || '');
        setValue('phone', currentUser.phone || '000000000');
        setValue('userIds', String(currentUser.id));

        const position = currentUser.employee_position;
        if (position) {
            setValue('employee_position_id', position.id);
            setSelectedPosition({ label: position.name, value: position.id });
        } else if (currentUser.employee_position_id) {
            getEmployeePositions(1).then((res) => {
                const match = res.data.find((p) => p.id === currentUser.employee_position_id);
                if (match) {
                    setValue('employee_position_id', match.id);
                    setSelectedPosition({ label: match.name, value: match.id });
                }
            });
        }

        if (currentUser.department_id) {
            setValue('department_id', currentUser.department_id);
            setSelectedDepartment({ label: currentUser.department?.name || '', value: currentUser.department_id });
        }
    }, [user, setValue]);

    return {
        t,
        register,
        handleSubmit,
        control,
        errors,
        onSubmit,
        selectedPosition,
        setSelectedPosition,
        loadPositionOptions,
        selectedDepartment,
        setSelectedDepartment,
        loadDepartmentOptions,
    };
};
